// Store checkbox state and util functions
class Checkbox {
  constructor(elem, idAttrName) {
    this.element = elem
    this.id = elem.getAttribute(idAttrName)
  }

  get isChecked() {
    return this.element.checked
  }

  check() {
    this.element.checked = true
  }

  uncheck() {
    this.element.checked = false
  }
}

// Store all checkboxes state
class CheckboxStore {
  constructor() {
    this.init()
  }

  updateCheckboxState(checkbox) {
    if (!(this.checkboxesCheckedList.has(checkbox.id) || this.checkboxesUncheckedList.has(checkbox.id))) {
      this.checkboxesById[checkbox.id] = checkbox.element
      this.checkboxesList.push(checkbox)
    }

    if (checkbox.isChecked) {
      if (!this.checkboxesCheckedList.has(checkbox.id)) {
        this.checkboxesCheckedList.add(checkbox.id)
      }

      if (this.checkboxesUncheckedList.has(checkbox.id)) {
        this.checkboxesUncheckedList.delete(checkbox.id)
      }
    } else {
      if (this.checkboxesCheckedList.has(checkbox.id)) {
        this.checkboxesCheckedList.delete(checkbox.id)
      }

      if (!this.checkboxesUncheckedList.has(checkbox.id)) {
        this.checkboxesUncheckedList.add(checkbox.id)
      }
    }

    this.updateGeneralState()
  }

  updateGeneralState() {
    let state = 'someChecked'
    if (this.checkboxesCheckedList.size == 0 && this.checkboxesUncheckedList.size != 0) {
      state = 'allUnchecked'
    } else if (this.checkboxesCheckedList.size != 0 && this.checkboxesUncheckedList.size == 0) {
      state = 'allChecked'
    }

    if (this.generalState != state) {
      this.previousUniqueGeneralState = this.generalState || null
      this.generalState = state
    }
  }

  init() {
    this.checkboxesList = new Array()
    this.checkboxesById = {}
    this.checkboxesCheckedList = new Set()
    this.checkboxesUncheckedList = new Set()
    this.generalState = null
    this.previousUniqueGeneralState = null

    this.updateGeneralState()
  }

  get checkboxesElementsChecked() {
    const checkboxes = []
    this.checkboxesCheckedList.forEach((id) => {
      checkboxes.push(this.checkboxesById[id])
    })
    return checkboxes
  }

  get checkboxesElementsUnchecked() {
    const checkboxes = []
    this.checkboxesUncheckedList.forEach((id) => {
      checkboxes.push(this.checkboxesById[id])
    })
    return checkboxes
  }

  get checkboxesCheckedCount() {
    return this.checkboxesCheckedList.size
  }

  get checkboxesUncheckedCount() {
    return this.checkboxesUncheckedList.size
  }
}

// Check all or uncheck all depending on a checkbox store
class CheckboxChecker {
  constructor(checkboxStore, checker) {
    this.checkboxStore = checkboxStore
    this.checker = checker
    this.toggleAll()
  }

  checkAll() {
    this.checkboxStore.checkboxesList.forEach((checkbox) => {
      checkbox.check()
      this.checkboxStore.updateCheckboxState(checkbox)
    })
    this.checker.checked = true
  }

  uncheckAll() {
    this.checkboxStore.checkboxesList.forEach((checkbox) => {
      checkbox.uncheck()
      this.checkboxStore.updateCheckboxState(checkbox)
    })
    this.checker.checked = false
  }

  refreshChecker() {
    const state = this.checkboxStore.generalState
    if (state == 'allChecked') {
      this.checker.checked = true
    } else if (state == 'allUnchecked') {
      this.checker.checked = false
    } else if (state == 'someChecked') {
      this.checker.checked = false
    }
  }

  toggleAll() {
    const state = this.checkboxStore.generalState
    if (state == 'allChecked') {
      this.uncheckAll()
    } else if (state == 'allUnchecked') {
      this.checkAll()
    } else if (state == 'someChecked') {
      this.checkAll()
    }
  }
}

// Update indicator element to represent a CheckboxStore the amount of
//  checkboxes checked depending on the CheckboxStore generalState.
//  Base -> Represents the total amount of checkboxes to take into account.
//          Wheather they have been added or not to the CheckboxStore yet.
class CheckAllIndicator {
  constructor(checkboxStore, counterElement, base) {
    this.counterElement = counterElement
    this.checkboxStore = checkboxStore
    this.base = base
  }

  updateIndicator() {
    let count = null
    const state = this.checkboxStore.generalState
    const prevState = this.checkboxStore.previousUniqueGeneralState

    if (state == 'allChecked') {
      count = this.base
    } else if (state == 'allUnchecked') {
      count = 0
    } else if (state == 'someChecked') {
      if (prevState == 'allChecked') {
        const checkboxesUncheckedCount = this.checkboxStore.checkboxesUncheckedList.size
        count = this.base - checkboxesUncheckedCount
      } else if (prevState == 'allUnchecked') {
        count = this.checkboxStore.checkboxesCheckedList.size
      } else {
        return null
      }
    } else {
      return null
    }
    this.counterElement.innerHTML = count.toLocaleString('en')
  }
}

// IMPORTANT: In order to avoid race condition like problems using this with stimulus,
//            call useCheckAll on afterUpdate method (inherited from ApplicationController).
export const useCheckAll = (c, idAttrName = 'data-reference-id', baseCount = null) => {
  // TARGETS NEEDED
  // - checkAllCheckbox -> checkboxes to track
  // - checkAllChecker -> checkbox to toggle all checkboxes
  // - checkAllCounter -> element that will have number counting amount of checkboxes checked
  const checkboxStore = new CheckboxStore()
  const checkboxChecker = new CheckboxChecker(checkboxStore, c.checkAllCheckerTarget)
  const checkAllIndicator = new CheckAllIndicator(checkboxStore, c.checkAllCounterTarget, baseCount)
  Object.assign(c, {

    useCheckAllState: {
      isAllChecked: false,
      checkboxStore: checkboxStore,
      checkboxChecker: checkboxChecker,
      checkAllIndicator: checkAllIndicator
    },

    updateCheckboxState: (event) => {
      c.updateCheckbox(event.currentTarget)
    },

    updateCheckbox: (checkbox) => {
      const store = c.useCheckAllState.checkboxStore
      store.updateCheckboxState(new Checkbox(checkbox, idAttrName))

      c.refreshIndicator()
      c.refreshChecker()
    },

    toggleAll: (event) => {
      c.useCheckAllState.checkboxChecker.toggleAll()
      c.refreshIndicator()
      c.refreshChecker()
    },

    checkAll: (event) => {
      c.useCheckAllState.checkboxChecker.checkAll()
      c.refreshIndicator()
      c.refreshChecker()
    },

    checkAllFromIndexToEnd(index) {
      const checkboxes = c.checkboxes()
      for (let i = index; i < checkboxes.length; i++) {
        checkboxes[i].check()
        c.useCheckAllState.checkboxStore.updateCheckboxState(checkboxes[i])
      }
      c.refreshIndicator()
      c.refreshChecker()
    },

    updateBase(amount) {
      c.useCheckAllState.checkAllIndicator.base = amount
      c.refreshIndicator()
    },

    refreshCheckboxesIncluded(event) {
      c.checkAllCheckboxTargets.forEach((checkbox) => {
        c.updateCheckbox(checkbox)
      })
      c.refreshIndicator()
      c.refreshChecker()
    },

    refreshIndicator(event) {
      c.useCheckAllState.checkAllIndicator.updateIndicator()
      const refreshedEvent = new CustomEvent(`${c.identifier}:useCheckAll:indicatorRefreshed`, { bubbles: true })
      setTimeout(() => { c.element.dispatchEvent(refreshedEvent) }, 0)
    },

    refreshChecker(event) {
      c.useCheckAllState.checkboxChecker.refreshChecker()
    },

    checkboxesUnchecked() {
      return c.useCheckAllState.checkboxStore.checkboxesElementsUnchecked
    },

    checkboxesUncheckedCount() {
      return c.useCheckAllState.checkboxStore.checkboxesUncheckedCount
    },

    checkboxesChecked() {
      return c.useCheckAllState.checkboxStore.checkboxesElementsChecked
    },

    checkboxesCheckedCount() {
      return c.useCheckAllState.checkboxStore.checkboxesCheckedCount
    },

    checkboxes() {
      return c.useCheckAllState.checkboxStore.checkboxesList
    },

    resetUseCheckAll(event) {
      c.useCheckAllState.checkboxStore.init()
      c.refreshCheckboxesIncluded(event)
    }
  })
}
